// src/components/Acceso/QRAccesoModal.jsx
import React, { useEffect, useState } from 'react';
import { Modal, Button, Typography, Tag, Space, Spin, Alert, Row, Col, Divider, message } from 'antd';
import {
  ReloadOutlined,
  ClockCircleOutlined,
  IdcardOutlined,
  QrcodeOutlined,
  KeyOutlined,
  CopyOutlined,
  CheckOutlined,
  FullscreenOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import Acceso from '../../service/Acceso';

// Cada cuántos ms el modal renueva el QR contra el backend.
const AUTO_REFRESH_MS = 30000;
// Solo se auto-renueva si a la ventana le queda poco. Con acceso por bloque (o
// permanente para staff) renovar cada 30 s sería reescribir la misma ventana en
// el torniquete una y otra vez sin cambiar nada.
const RENOVAR_BAJO_MS = 5 * 60 * 1000;

const { Title, Text } = Typography;

const formatCountdown = (ms) => {
  if (ms <= 0) return '00:00';
  const total = Math.floor(ms / 1000);
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const formatPin = (pin) => {
  if (!pin) return '';
  return String(pin).replace(/(.{3})/g, '$1 ').trim();
};

/**
 * Modal reutilizable que muestra el acceso al recinto en dos opciones equivalentes:
 *   1. Lee el QR en el lector.
 *   2. Teclea el PIN en el panel.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - loading: boolean
 * - error: string | null
 * - data: { qrDataUrl, pin, expiraEn, rolUsado, contextoTaller } | null
 * - onRegenerar: () => void
 */
export default function QRAccesoModal({ open, onClose, loading, error, errorStatus, data, onRegenerar }) {
  const [restanteMs, setRestanteMs] = useState(0);
  const [copiado, setCopiado] = useState(false);
  const [qrAmpliado, setQrAmpliado] = useState(false);
  const [currentData, setCurrentData] = useState(data);
  const [refrescando, setRefrescando] = useState(false);

  // Sincroniza el estado interno cuando el padre fuerza una recarga (botón "Regenerar")
  // o cambia el QR inicial.
  useEffect(() => {
    setCurrentData(data);
  }, [data]);

  useEffect(() => {
    if (!currentData?.expiraEn) return;
    const tick = () => setRestanteMs(new Date(currentData.expiraEn).getTime() - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [currentData?.expiraEn]);

  // Auto-renueva el QR mientras el modal está abierto. Cada renovación re-upserta
  // la ventana de acceso en ZKBio y rota el `&t=` del QR (visualmente distinto).
  // Dep es bool para no resetear el interval en cada renovación exitosa.
  const tieneQR = !!currentData?.qrDataUrl;
  const esPermanente = currentData?.tipoVentana === 'permanente';
  const ventanaLarga =
    esPermanente || (currentData?.expiraEn &&
      new Date(currentData.expiraEn).getTime() - Date.now() > RENOVAR_BAJO_MS);

  useEffect(() => {
    if (!open || !tieneQR || ventanaLarga) return;
    const id = setInterval(async () => {
      try {
        setRefrescando(true);
        // Se renueva el acceso de ESTE inscrito, no el del primero de la lista.
        const fresh = await Acceso.GenerarQR(currentData?.inscripcionId);
        setCurrentData(fresh);
      } catch {
        // Silencioso: el botón "Regenerar" sigue disponible para reintento manual.
      } finally {
        setRefrescando(false);
      }
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [open, tieneQR, ventanaLarga, currentData?.inscripcionId]);

  // Cambia el acceso mostrado al de otro inscrito de la misma cuenta (un familiar
  // con clase a la misma hora). Cada uno abre con su propio RUT.
  const handleCambiarInscrito = async (inscripcionId) => {
    try {
      setRefrescando(true);
      setCurrentData(await Acceso.GenerarQR(inscripcionId));
    } catch (e) {
      message.error(e?.message || 'No se pudo generar el acceso de esa persona');
    } finally {
      setRefrescando(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setCopiado(false);
      setQrAmpliado(false);
    }
  }, [open]);

  const expirado = currentData?.expiraEn && restanteMs <= 0;

  const handleCopyPin = async () => {
    if (!currentData?.pin) return;
    try {
      await navigator.clipboard.writeText(currentData.pin);
      setCopiado(true);
      message.success('PIN copiado');
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      message.warning('No se pudo copiar automáticamente. Selecciona y copia manualmente.');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={620}
      title={
        <Space>
          <IdcardOutlined style={{ color: '#0047bb' }} />
          <span>Acceso al recinto</span>
        </Space>
      }
      footer={[
        onRegenerar && (
          <Button key="regenerar" icon={<ReloadOutlined />} onClick={onRegenerar} loading={loading}>
            Regenerar
          </Button>
        ),
        <Button
          key="cerrar"
          type="primary"
          onClick={onClose}
          style={{ background: '#0047bb', borderColor: '#0047bb' }}
        >
          Cerrar
        </Button>,
      ].filter(Boolean)}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: '#64748b' }}>Generando acceso...</div>
        </div>
      )}

      {/* 403 no es una falla: es el backend diciendo que todavía no corresponde
          (sin inscripción vigente o fuera del bloque). Se explica en vez de alarmar. */}
      {!loading && error && errorStatus === 403 && (
        <Alert
          type="info"
          showIcon
          message="Todavía no puedes generar tu acceso"
          description={
            <>
              <div>{error}</div>
              <div style={{ marginTop: 6, fontSize: 12, color: '#475569' }}>
                El acceso se habilita desde 30 minutos antes de tu clase y dura hasta el fin del día.
                Si tienes un taller hoy, revisa en «Mis talleres» que la inscripción esté vigente.
              </div>
            </>
          }
        />
      )}
      {!loading && error && errorStatus !== 403 && (
        <Alert type="error" message="No se pudo generar el acceso" description={error} showIcon />
      )}

      {!loading && !error && currentData && (
        <div>
          {currentData.contextoTaller && (
            <div style={{ marginBottom: 14, textAlign: 'center' }}>
              <Space size={[6, 6]} wrap>
                <Tag color="blue" style={{ fontSize: 13, padding: '4px 10px' }}>
                  {currentData.contextoTaller.nombre}
                </Tag>
                {currentData.contextoTaller.recinto && (
                  <Tag color="default" style={{ fontSize: 12 }}>
                    {currentData.contextoTaller.recinto}
                  </Tag>
                )}
                {currentData.contextoTaller.bloque && (
                  <Tag color="blue" style={{ fontSize: 12 }}>
                    {currentData.contextoTaller.bloque}
                  </Tag>
                )}
              </Space>
            </div>
          )}

          {/* De quién es el acceso. Con familiares inscritos el titular de la cuenta
              no es necesariamente quien entra, así que hay que decirlo explícito. */}
          {currentData.titular?.nombre && (
            <div style={{ textAlign: 'center', marginBottom: 10 }}>
              <Text style={{ fontSize: 14 }}>
                Acceso de <strong>{currentData.titular.nombre}</strong>
              </Text>
              {currentData.titular.rut && (
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  RUT {currentData.titular.rut}
                </Text>
              )}
            </div>
          )}

          {currentData.otrosInscritos?.length > 0 && (
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                También tienes clase ahora para:
              </Text>
              <Space size={[6, 6]} wrap>
                {currentData.otrosInscritos.map((otro) => (
                  <Button
                    key={otro.inscripcionId}
                    size="small"
                    onClick={() => handleCambiarInscrito(otro.inscripcionId)}
                    loading={refrescando}
                  >
                    {otro.nombre}
                  </Button>
                ))}
              </Space>
            </div>
          )}

          <Text
            type="secondary"
            style={{ display: 'block', textAlign: 'center', fontSize: 13, marginBottom: 16 }}
          >
            En el torniquete elige <strong>una</strong> de las dos opciones
          </Text>

          <Row gutter={[16, 16]} align="stretch">
            {/* ── Opción 1: QR ───────────────────────────────────────────── */}
            <Col xs={24} sm={12}>
              <div
                style={{
                  border: '1px solid #e8e8e8',
                  borderRadius: 12,
                  padding: 16,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  background: expirado ? '#fafafa' : '#fff',
                  opacity: expirado ? 0.5 : 1,
                }}
              >
                <Space style={{ marginBottom: 10 }}>
                  <QrcodeOutlined style={{ fontSize: 18, color: '#0047bb' }} />
                  <Text strong style={{ fontSize: 14 }}>
                    Opción 1 · Escanea el QR
                  </Text>
                </Space>
                <div
                  onClick={() => !expirado && setQrAmpliado(true)}
                  style={{
                    background: '#fff',
                    padding: 6,
                    borderRadius: 8,
                    cursor: expirado ? 'default' : 'pointer',
                    position: 'relative',
                  }}
                  title="Toca para ampliar"
                >
                  {/* El QR lo genera el backend con el formato URL del carnet chileno
                      que el lector del torniquete sabe parsear. Generarlo aquí con
                      solo el PIN produce un QR que el lector descarta. */}
                  <img
                    src={currentData.qrDataUrl}
                    alt="QR de acceso"
                    width={260}
                    height={260}
                    style={{ display: 'block', imageRendering: 'pixelated' }}
                  />
                  {!expirado && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 10,
                        right: 10,
                        background: 'rgba(0, 71, 187, 0.92)',
                        color: '#fff',
                        borderRadius: 999,
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        pointerEvents: 'none',
                      }}
                    >
                      <FullscreenOutlined />
                    </div>
                  )}
                </div>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, marginTop: 10, textAlign: 'center' }}
                >
                  Toca el QR para ampliarlo y acércalo al lector
                </Text>
              </div>
            </Col>

            {/* ── Opción 2: PIN ──────────────────────────────────────────── */}
            <Col xs={24} sm={12}>
              <div
                style={{
                  border: '1px solid #e8e8e8',
                  borderRadius: 12,
                  padding: 16,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: expirado ? '#fafafa' : '#fff',
                  opacity: expirado ? 0.5 : 1,
                }}
              >
                <Space style={{ marginBottom: 10 }}>
                  <KeyOutlined style={{ fontSize: 18, color: '#0047bb' }} />
                  <Text strong style={{ fontSize: 14 }}>
                    Opción 2 · Teclea tu PIN
                  </Text>
                </Space>

                <div
                  style={{
                    background: '#f1f5f9',
                    border: '2px dashed #cbd5e1',
                    borderRadius: 10,
                    padding: '20px 12px',
                    width: '100%',
                    textAlign: 'center',
                    margin: '8px 0',
                  }}
                >
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      letterSpacing: 4,
                      fontFamily: 'monospace',
                      color: '#0f172a',
                      fontSize: 32,
                    }}
                  >
                    {formatPin(currentData.pin)}
                  </Title>
                </div>

                <Button
                  size="small"
                  icon={copiado ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={handleCopyPin}
                  style={{ marginTop: 4 }}
                >
                  {copiado ? 'Copiado' : 'Copiar PIN'}
                </Button>

                <Text
                  type="secondary"
                  style={{ fontSize: 11, marginTop: 10, textAlign: 'center' }}
                >
                  Ingrésalo en el teclado del panel
                </Text>
              </div>
            </Col>
          </Row>

          <Divider style={{ margin: '18px 0 12px' }} />

          <div style={{ textAlign: 'center' }}>
            {expirado ? (
              <Tag color="red" style={{ fontSize: 13, padding: '4px 10px' }}>
                Acceso expirado — genera uno nuevo
              </Tag>
            ) : esPermanente ? (
              <Tag color="green" style={{ fontSize: 13, padding: '4px 10px' }}>
                Acceso permanente
              </Tag>
            ) : (
              <Tag
                color="orange"
                icon={<ClockCircleOutlined />}
                style={{ fontSize: 13, padding: '4px 10px' }}
              >
                Válido hasta las {dayjs(currentData.expiraEn).format('HH:mm')}
                {/* La cuenta regresiva recién aporta cuando queda poco: un bloque
                    de dos horas mostraría "120:00", que no dice nada. */}
                {restanteMs < 60 * 60 * 1000 && ` (${formatCountdown(restanteMs)})`}
              </Tag>
            )}
            {currentData.rolUsado === 'staff' && (
              <Tag color="purple" style={{ fontSize: 12, marginLeft: 6 }}>
                Acceso staff
              </Tag>
            )}
            {!expirado && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {refrescando ? (
                    <>
                      <Spin size="small" style={{ marginRight: 6 }} /> Actualizando QR…
                    </>
                  ) : esPermanente ? (
                    'También puedes entrar directamente con tu cédula'
                  ) : (
                    'Durante este bloque también puedes entrar con tu cédula'
                  )}
                </Text>
              </div>
            )}
          </div>
        </div>
      )}

      {qrAmpliado && currentData?.qrDataUrl && (
        <div
          onClick={() => setQrAmpliado(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: '#fff',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            cursor: 'pointer',
          }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setQrAmpliado(false); }}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 44,
              height: 44,
              borderRadius: 999,
              border: 'none',
              background: '#f1f5f9',
              color: '#0f172a',
              fontSize: 18,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Cerrar"
          >
            <CloseOutlined />
          </button>
          <img
            src={currentData.qrDataUrl}
            alt="QR de acceso ampliado"
            style={{
              width: 'min(90vmin, 100%)',
              height: 'auto',
              imageRendering: 'pixelated',
            }}
          />
          <Text type="secondary" style={{ marginTop: 24, fontSize: 13 }}>
            Acerca el QR al lector del torniquete
          </Text>
          <Text type="secondary" style={{ marginTop: 4, fontSize: 11 }}>
            Toca cualquier parte para cerrar
          </Text>
        </div>
      )}
    </Modal>
  );
}
